import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'professional' ou 'parent'
  const [accountStatus, setAccountStatus] = useState(null); // 'en_attente_validation' | 'valide' | 'refuse'
  const [loading, setLoading] = useState(true);

useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        detectUserType(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        detectUserType(session.user.id);
      } else if (_event === 'SIGNED_OUT') {
        setUser(null);
        setUserType(null);
        setAccountStatus(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const detectUserType = async (userId) => {
    try {
      // Étape 1 : détection du type — ne dépend PAS de la colonne account_status,
      // pour ne jamais mal-router un compte même si la migration SQL n'a pas encore été jouée.
      const [parentRes, patientRes] = await Promise.all([
        supabase.from('parents').select('id').eq('id', userId).maybeSingle(),
        supabase.from('patients').select('id').eq('id', userId).maybeSingle(),
      ]);

      let type = 'professional';
      let table = 'professional_profiles';
      if (parentRes.data) {
        type = 'parent';
        table = 'parents';
      } else if (patientRes.data) {
        type = 'patient';
        table = 'patients';
      }
      setUserType(type);

      // Étape 2 : statut de validation, isolé — si la colonne account_status n'existe pas
      // encore (migration pas jouée) ou que la requête échoue, on ne bloque pas l'accès
      // (comportement identique à avant l'ajout de cette fonctionnalité) mais on le signale en console.
      const { data: statusRow, error: statusError } = await supabase
        .from(table)
        .select('account_status')
        .eq('id', userId)
        .maybeSingle();

      if (statusError) {
        console.warn('Impossible de lire account_status (migration SQL pas encore appliquée ?) :', statusError.message);
        setAccountStatus('valide');
      } else {
        setAccountStatus(statusRow?.account_status || 'valide');
      }
    } catch (error) {
      console.error('Error detecting user type:', error);
      setUserType('professional');
      setAccountStatus('valide');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setUser(data.user);
    detectUserType(data.user.id);
    return data;
  };
  
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserType(null);
    setAccountStatus(null);
  };

  return (
    <AuthContext.Provider value={{ user, userType, accountStatus, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
